import type { MessageKey } from '@ecom/i18n';

/**
 * Migration 0097 — where an order came from (`orders.utm_data`, decided by the storefront on the visitor's first
 * page, or the channel the merchant picked for a manual entry). Labels and colours shared by the order badge, the
 * manual-entry form and « Ventes par réseau ».
 */
export interface OrderAttribution {
  readonly source: string;
  readonly medium: string;
  readonly campaign: string | null;
  readonly detail: string | null;
  readonly via: 'utm' | 'click_id' | 'in_app' | 'referrer' | 'none' | 'manual';
  readonly firstSeenAt: string;
}

const LABELS: Readonly<Record<string, string>> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  messenger: 'Messenger',
  whatsapp: 'WhatsApp',
  tiktok: 'TikTok',
  snapchat: 'Snapchat',
  youtube: 'YouTube',
  x: 'X',
  linkedin: 'LinkedIn',
  telegram: 'Telegram',
  pinterest: 'Pinterest',
  google: 'Google',
  bing: 'Bing',
};

/** Sources whose name is a word to translate rather than a brand. */
const TRANSLATED: Readonly<Record<string, MessageKey>> = {
  email: 'merchant.attribution.email',
  referral: 'merchant.attribution.referral',
  other: 'merchant.attribution.other',
  direct: 'merchant.attribution.direct',
  phone: 'merchant.attribution.phone',
  in_store: 'merchant.attribution.inStore',
  manual: 'merchant.attribution.manual',
  unknown: 'merchant.attribution.unknown',
};

/** Brand-ish colour per source, for the breakdown bars (neutral for the rest). */
export const SOURCE_COLOR: Readonly<Record<string, string>> = {
  facebook: '#1877F2',
  instagram: '#D62976',
  messenger: '#0084FF',
  whatsapp: '#25D366',
  tiktok: '#111111',
  snapchat: '#E5C700',
  youtube: '#FF0000',
  x: '#3A3A3A',
  linkedin: '#0A66C2',
  telegram: '#229ED9',
  pinterest: '#E60023',
  google: '#34A853',
  bing: '#008373',
  // Not a social network: colours of their own, unlike any brand above, so each one stands apart in the ring.
  unknown: '#94A3B8', // slate — « Non renseigné »
  manual: '#5B6B3A', // olive — « Saisie manuelle »
  direct: '#9E3A15', // the shop's own dye — « Accès direct »
  referral: '#2D5A85', // deep blue — « Autre site »
  other: '#78716C', // stone
  phone: '#B45309', // brown — « Téléphone »
  in_store: '#A16207', // dark gold — « En boutique »
  email: '#64748B', // dark slate
};

export function sourceLabel(source: string, t: (key: MessageKey) => string): string {
  const key = TRANSLATED[source];
  return key !== undefined ? t(key) : (LABELS[source] ?? source);
}

/** The channels a merchant can pick for a manual order or sale (mirrors the API's `MANUAL_CHANNELS`). */
export const MANUAL_CHANNELS = [
  'instagram',
  'facebook',
  'messenger',
  'whatsapp',
  'tiktok',
  'phone',
  'in_store',
  'other',
] as const;
export type ManualChannel = (typeof MANUAL_CHANNELS)[number];

/** Adds the UTM parameters that make the storefront attribute a visit to this link (`?utm_source=…`). */
export function withUtm(
  url: string,
  source: string,
  medium = 'social',
  campaign = 'souk_share',
): string {
  try {
    const target = new URL(url);
    target.searchParams.set('utm_source', source);
    target.searchParams.set('utm_medium', medium);
    target.searchParams.set('utm_campaign', campaign);
    return target.toString();
  } catch {
    return url;
  }
}
